import { createActions } from 'redux-actions';

import { ENDPOINTS_MESSAGES } from '../config/';
import { localeData } from '../locales/data';
import { MessagesDataObjectProps, MessagesDataProps, MessagesDataSummary } from '../reducers/MessagesReducer/MessagesReducer.types';
import { globalFetch, isAction } from '../shared/helpers/fetch-helper';
import { logErrorService } from '../shared/services';

import { addFlashMessages } from './FlashMessagesActions';
import { invalidRequest } from './GenericActions';

export const {
  responseMessages,
  responseLastMessages,
  responseSummary,
  responseMessageMarkRead,
  requestMessages,
  responseMessagesFailure,
  requestLastMessages,
  responseLastMessagesFailure,
  requestMessageMarkRead,
  setType,
  setFilter,
  responseMessageMarkReadFailure,
  makeItScroll,
  stopScroll,
} = createActions(
  {
    REQUEST_MESSAGES: (type?: string) => ({ type }),
    RESPONSE_MESSAGES: (data: MessagesDataProps, type: string) => ({ data, type }),
    RESPONSE_LAST_MESSAGES: (data: MessagesDataProps) => ({ data }),
    RESPONSE_SUMMARY: (data: MessagesDataProps | MessagesDataSummary | MessagesDataObjectProps) => ({ data }),
    RESPONSE_MESSAGE_MARK_READ: (data: MessagesDataObjectProps, type: string, id: string) => ({ data, type, id }),
    RESPONSE_LAST_MESSAGES_FAILURE: (type?: string) => ({ type }),
    SET_TYPE: (currentType: string) => ({ currentType }),
    SET_FILTER: (currentFilter: string) => ({ currentFilter }),
  },
  'RESPONSE_MESSAGES_FAILURE',
  'REQUEST_LAST_MESSAGES',
  'REQUEST_MESSAGE_MARK_READ',
  'RESPONSE_MESSAGE_MARK_READ_FAILURE',
  'MAKE_IT_SCROLL',
  'STOP_SCROLL'
);

export function getLastMessages(limit: number = 5) {
  return async (dispatch: any): Promise<object> => {
    dispatch(requestLastMessages());

    return globalFetch(
      `${ENDPOINTS_MESSAGES.messages}/self/messages?order=-dateCreated&limit=${limit}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseLastMessagesFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_LAST_MESSAGES_FAILURE')) {
          return json;
        } else {
          return dispatch(responseLastMessages(json));
        }
      })
      .catch((err: any) => {
        dispatch(
          addFlashMessages([
            { msg: 'notifications.error.get.last.messages', defaultMessage: localeData.en['notifications.error.get.last.messages'] },
          ])
        );
        logErrorService(err, err);

        return dispatch(responseLastMessagesFailure());
      });
  };
}

export const getMyMessages =
  (type: string, offset: number = 0, typeAggregate: string = 'unread', loader: boolean = true) =>
  async (dispatch: any): Promise<object> => {
    let endpoint: string = `${ENDPOINTS_MESSAGES.messages}/self/messages?type=${type}&order=-dateCreated&limit=10&offset=${offset}`;

    if (typeAggregate === 'unread') {
      endpoint = `${endpoint}&read=false`;
    }

    if (loader) {
      dispatch(requestMessages(type));
    }

    return globalFetch(
      endpoint,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseMessagesFailure(type))
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_MESSAGES_FAILURE')) {
          return json;
        } else {
          return dispatch(responseMessages(json, type.toLocaleLowerCase()));
        }
      })
      .catch((err: any) => {
        dispatch(
          addFlashMessages([{ msg: 'notifications.error.get.messages', defaultMessage: localeData.en['notifications.error.get.messages'] }])
        );
        logErrorService(err, err);

        return dispatch(responseMessagesFailure());
      });
  };

export function getSummary() {
  return async (dispatch: any): Promise<object> =>
    globalFetch(
      `${ENDPOINTS_MESSAGES.messages}/self/messages/summary`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseMessagesFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_MESSAGES_FAILURE')) {
          return json;
        } else {
          return dispatch(responseSummary(json));
        }
      })
      .catch((err: any) => {
        dispatch(
          addFlashMessages([
            { msg: 'notifications.error.get.messages.summary', defaultMessage: localeData.en['notifications.error.get.messages.summary'] },
          ])
        );
        logErrorService(err, err);

        return dispatch(responseMessagesFailure());
      });
}

export function markAsRead(messageId: string, typeAggregate: string, offset: number) {
  return async (dispatch: any): Promise<object> => {
    if (messageId === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestMessageMarkRead(messageId));

    return globalFetch(
      `${ENDPOINTS_MESSAGES.message}/${messageId}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ read: true }),
      },
      dispatch,
      () => dispatch(responseMessageMarkReadFailure(messageId))
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_MESSAGE_MARK_READ_FAILURE')) {
          return json;
        } else {
          dispatch(responseMessageMarkRead(json, json.type.toLocaleLowerCase(), messageId));
          if (typeAggregate === 'unread') {
            dispatch(getMyMessages(json.type, offset, typeAggregate, false));
          }

          return dispatch(getSummary());
        }
      })
      .catch((err: any) => {
        dispatch(
          addFlashMessages([
            { msg: 'notifications.error.mark.messages', defaultMessage: localeData.en['notifications.error.mark.messages'] },
          ])
        );
        logErrorService(err, err);

        return dispatch(responseMessageMarkReadFailure(messageId));
      });
  };
}

export const markAllAsRead =
  (messageUnread: any, typeAggregate: string) =>
  async (dispatch: any): Promise<object> => {
    dispatch(requestMessages());

    return globalFetch(
      `${ENDPOINTS_MESSAGES.messages}/self/messages/summary`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(messageUnread),
      },
      dispatch,
      () => dispatch(responseMessagesFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_MESSAGES_FAILURE')) {
          return json;
        } else {
          dispatch(getMyMessages(Object.keys(messageUnread)[0].toUpperCase(), 0, typeAggregate));

          return dispatch(responseSummary(json));
        }
      })
      .catch((err: any) => {
        dispatch(
          addFlashMessages([
            { msg: 'notifications.error.mark.all.messages', defaultMessage: localeData.en['notifications.error.mark.all.messages'] },
          ])
        );
        logErrorService(err, err);

        return dispatch(responseMessagesFailure());
      });
  };
